import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, TrendingDown, Zap, Eye, TrendingUp, CreditCard, MapPin, Heart, Handshake } from "lucide-react";
import useSEO from "@/lib/useSEO";
import { IMG } from "@/lib/images";

const benefits = [
  { icon: TrendingDown, title: "Reduce costs", desc: "Lower fees on every transaction — savings that go straight back into your business." },
  { icon: Zap, title: "Faster payments", desc: "Modern terminals that settle next-day, not next-week." },
  { icon: Eye, title: "The right kit", desc: "We match you with the payment hardware and partners that fit how you actually trade." },
  { icon: TrendingUp, title: "Drive growth", desc: "Insights, digital tools, and a team that actually picks up the phone." },
];

export default function Home() {
  useSEO({
    title: "Smart solutions. Real results.",
    description: "Fenco Digital helps UK businesses reduce costs and grow with smarter card payment solutions. Lower fees, faster payouts, modern terminals — local Northern Ireland team.",
    path: "/",
  });
  return (
    <div data-testid="home-page">
      {/* HERO — full-bleed background image with overlay */}
      <section
        className="relative overflow-hidden min-h-[88vh] flex items-center"
        data-testid="hero-section"
      >
        {/* Background image */}
        <div className="absolute inset-0 z-0">
          <img
            src={IMG.cardInUseHero}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover"
            data-testid="hero-bg-image"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/65 to-black/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-28 w-full">
          <div className="max-w-3xl fade-up">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/20">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F]" />
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-white">
                Card Payments · Lower Fees · Faster Payouts
              </span>
            </div>

            <h1
              className="font-display text-6xl md:text-8xl lg:text-[9rem] font-extrabold tracking-tighter leading-[0.95] text-white mt-7"
              data-testid="hero-headline"
            >
              Fenco<br />
              Digital<span className="text-[#FF5A1F]">.</span>
            </h1>

            <p
              className="mt-8 text-xl md:text-2xl text-white/90 font-medium leading-snug max-w-2xl"
              data-testid="hero-tagline"
            >
              Reduce card machine costs<br className="hidden sm:block" /> &amp; grow your business.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/get-started"
                data-testid="hero-get-quote-button"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 text-base font-semibold text-white bg-[#FF5A1F] hover:bg-[#E04B15] rounded-full transition-all duration-200 shadow-lg shadow-orange-500/30 hover:-translate-y-0.5"
              >
                Get a Quote <ArrowRight size={18} />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-contact-button"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 text-base font-semibold text-white bg-white/10 backdrop-blur border border-white/30 hover:bg-white/20 rounded-full transition-all duration-200 hover:-translate-y-0.5"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* WELCOME / ABOUT — text only, no image */}
      <section className="relative py-24 md:py-32 bg-white overflow-hidden" data-testid="welcome-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 leading-[1.05]">
            Welcome to Fenco Digital<span className="text-[#FF5A1F]">.</span>
          </h2>

          <p className="mt-7 text-lg md:text-xl text-gray-600 leading-relaxed">
            We're a <strong className="text-gray-900">local Northern Ireland company</strong> on a simple mission — helping businesses across the country <strong className="text-gray-900">reduce costs and grow</strong> with smarter card payment solutions.
          </p>

          <p className="mt-5 text-base md:text-lg text-gray-600 leading-relaxed">
            We work closely with cafés, retailers, restaurants and service businesses — picking the right kit, switching merchant providers, and making sure the till never lets you down. No jargon. No surprises. Just honest advice and tech that actually pays for itself.
          </p>

          <div className="mt-12 grid sm:grid-cols-3 gap-8 text-left max-w-3xl mx-auto">
            {[
              { icon: MapPin, title: "Local & on the ground", desc: "Northern Ireland based, supporting UK-wide." },
              { icon: Heart, title: "Built on trust", desc: "Honest pricing. No hidden fees. Ever." },
              { icon: Handshake, title: "We're with you", desc: "Ongoing professional support — from quote to install and beyond." },
            ].map((it, i) => (
              <div key={it.title} data-testid={`welcome-pillar-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-orange-50 flex items-center justify-center mb-3">
                  <it.icon className="text-[#FF5A1F]" size={20} />
                </div>
                <h4 className="font-display text-base font-bold text-gray-900">{it.title}</h4>
                <p className="text-sm text-gray-600 mt-1.5 leading-relaxed">{it.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OVERVIEW + BENEFITS */}
      <section className="relative py-24 md:py-32 bg-[#F8F9FA]" data-testid="overview-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-16">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF5A1F]">Smart payments. Smarter business.</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mt-4">
              Making your business work harder.
            </h2>
          </div>

          <div className="mb-12">
            <Link
              to="/card-payments"
              data-testid="overview-card-payments"
              className="group relative block overflow-hidden rounded-3xl bg-[#0a0a0a] text-white p-10 md:p-12 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <div className="absolute -right-20 -top-20 w-96 h-96 bg-[#FF5A1F]/10 blur-3xl rounded-full" />
              <div className="relative grid md:grid-cols-12 gap-8 items-center">
                <div className="md:col-span-9">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-2xl bg-[#FF5A1F]/15 flex items-center justify-center border border-[#FF5A1F]/20">
                      <CreditCard className="text-[#FF5A1F]" size={26} />
                    </div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A1F]">Card Payment Solutions</p>
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
                    Lower fees. Faster payments. <br className="hidden md:block" />
                    <span className="text-[#FF5A1F]">Modern terminals.</span>
                  </h3>
                  <p className="mt-5 text-gray-300 leading-relaxed text-lg max-w-2xl">
                    We work with the right partner for your business — helping you cut card machine costs, take faster payments, and get next-day settlement so cash flow keeps up with the till.
                  </p>
                </div>
                <div className="md:col-span-3 flex md:justify-end">
                  <span className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-[#0a0a0a] bg-white group-hover:bg-[#FF5A1F] group-hover:text-white rounded-full transition-all duration-200">
                    Learn more <ArrowUpRight size={16} />
                  </span>
                </div>
              </div>
            </Link>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {benefits.map((b, i) => (
              <div
                key={b.title}
                data-testid={`benefit-card-${i}`}
                className="bg-white border border-gray-100 rounded-2xl p-6 hover:shadow-lg hover:border-gray-200 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-xl bg-orange-50 flex items-center justify-center mb-4">
                  <b.icon className="text-[#FF5A1F]" size={20} />
                </div>
                <h4 className="font-display text-base md:text-lg font-bold text-gray-900">{b.title}</h4>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
